import { isoDateTime, z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";

// THE AUDIT TRAIL, READABLE (spec 16 §2.16, §4 S17, §5).
//
// The platform has written an append-only, hash-chained `audit_log` row on every
// mutation since migration 0001 — who changed a price, who released a
// quarantine, who overrode a calibration, who disposed a hold. Until this
// contract, NO CUSTOMER COULD READ ANY OF IT: `openapi/v1.json` contained zero
// paths matching "audit", and the only way to answer "who touched this drum?"
// was a psql session. That is the compliance asset a $25M-turnover manufacturer
// is asked for in a customer audit or a dispute, and it was invisible.
//
// It is a READ surface over rows that already exist. Nothing here writes,
// amends, or re-hashes anything; `audit_log` remains INSERT+SELECT only at the
// grant level (0001) with UPDATE/DELETE/TRUNCATE revoked from both app roles.
//
// TWO QUESTIONS, TWO SHAPES:
//   * `GET /v1/audit/entries`      — "what happened?", filterable by entity,
//     actor, action and date range, keyset-paginated by `seq`.
//   * `GET /v1/audit/chain/verify` — "is my trail intact?", a contiguous
//     unfiltered seq range walked with the SAME hash primitive the writer and
//     the test-kit verifier share (`chainHash`, packages/db). It is a second
//     walker, not `verifyChain` itself, because a filtered page needs
//     page-scoped link semantics that `verifyChain` (which requires contiguity
//     from seq 1) cannot express; their agreement over a whole chain is pinned
//     by a unit test, in both the intact and the tampered direction.
//
// WHAT THIS SURFACE DOES NOT SHOW, said here rather than discovered later:
// PRE-TENANT auth events — every login, logout, lockout and password reset —
// carry `tenant_id NULL` and land on the `'global'` chain (spec 16 §2.16), and
// this endpoint serves the tenant chain only. What the `identity.*` rows here
// cover is the in-tenant identity work: the org tree, roles, memberships,
// invites and impersonated requests. The device/login history is the separate
// `auth_events` surface.
//
// EVERY VERIFICATION ANSWER STATES ITS OWN SCOPE. A list page is a FILTERED
// window: its rows are usually not consecutive, so "the links hold" can only
// ever be said about the adjacent pairs that happen to be present. Saying `ok:
// true` without saying what was checked would be a lie of omission on precisely
// the surface whose product is trustworthiness — so both shapes below carry the
// scope, the counts, and whether the range reaches the genesis row.

/** The chain sequence number: dense and monotonic per tenant chain (0001). */
export const AuditSeq = z.number().int().positive();

/**
 * The parsed shape of the `actor` column, whose grammar is spec 16 §2.16:
 * `user:<uuid>` | `anonymous` | `system` | `platform_staff:<uuid> via grant:<id>`.
 * Served alongside the raw string rather than instead of it — the raw value is
 * what the row was HASHED with, so quoting it in a dispute is the point.
 */
export const AuditActorKind = z
  .enum(["user", "system", "anonymous", "platform_staff", "unknown"])
  .openapi("AuditActorKind");

export const AuditEntry = z
  .object({
    id: Uuid,
    /** Position in this tenant's chain. Ordering key AND pagination cursor. */
    seq: AuditSeq,
    /** The app-clock instant the row was hashed with (ISO-8601, ms, UTC). */
    occurred_at: isoDateTime(),
    /** Verbatim `actor` column — the hashed value. */
    actor: z.string(),
    actor_kind: AuditActorKind,
    /** Set when `actor_kind === 'user'`; the tenant user who acted. */
    actor_user_id: Uuid.optional(),
    /** Set under impersonation — the platform staff member behind the act. */
    actor_staff_id: Uuid.optional(),
    /** `<context>.<thing_happened>`, e.g. `quality.hold_released`. */
    action: z.string(),
    /** Free `text` at the DB level; NULL for pre-tenant + request-level rows. */
    entity_type: z.string().nullable(),
    /** Usually a uuid, but NOT always — two writers use a natural key. */
    entity_id: z.string().nullable(),
    /**
     * The module's own payload. ABSENT — not null, not masked — when the caller
     * does not hold the originating module's read permission
     * (packages/contracts audit/payload-permissions.ts). Absence is the house
     * spelling of redaction (D-074 pt 3): a mask is a value, and absence is not.
     */
    payload: z.record(z.string(), z.unknown()).optional(),
    /**
     * Whether `payload` was withheld. Present ALWAYS, and it is metadata about
     * the redaction rather than a masked value: `payload` defaults to `{}` in
     * the database, so "empty object" is a real answer that a reader must be
     * able to tell apart from "not for you".
     */
    payload_redacted: z.boolean(),
    /**
     * The codes the caller would need to see this payload. Present only when
     * `payload_redacted` is true AND a rule is registered for the action; its
     * absence there means no rule exists yet and the payload is withheld by the
     * closed default.
     */
    payload_requires: z.array(z.string()).optional(),
    // NO HASH IS SERVED (D-337 ruling 3). `prev_hash` and `curr_hash` were on
    // this object until D-337. The chain is unkeyed sha256 over the row and,
    // through `prev_hash`, over every row before it, so any row's `curr_hash`
    // commits to every earlier payload. A reader served the envelopes and two
    // hashes either side of a withheld payload could test guessed payloads
    // offline until one matched, and a short money payload is a small space
    // to guess in. Dropping the hashes of the withheld row and its successor
    // does not close that, because every later hash still commits to it. A
    // reader who may read every payload up to a row can recompute that row's
    // hash from what is served, so serving it gave that reader nothing new.
    // `chain` and `GET /v1/audit/chain/verify` recompute from the stored rows
    // on the server and say what held. A keyed blind (D-301) is how a hash
    // could come back.
  })
  .openapi("AuditEntry");

/**
 * What was verified about the rows ON THIS PAGE — and, just as importantly,
 * what was not.
 *
 * A filtered page is a sample of the chain. Every returned row's `curr_hash` is
 * recomputed from its content (that is a complete statement about the row), but
 * a LINK can only be checked where two consecutive `seq` values both appear in
 * the page. `links_checked` is therefore usually far below `rows_checked - 1`,
 * and `contiguous` says outright whether the page is a run.
 */
export const AuditPageIntegrity = z
  .object({
    scope: z.literal("page"),
    /** Rows the page returned — the denominator, not a claim about checking.
     * On a break the walk stops, so `hashes_recomputed` is what was actually
     * verified and this stays the page size. */
    rows_checked: z.number().int().nonnegative(),
    /** Rows whose `curr_hash` recomputed from their content. */
    hashes_recomputed: z.number().int().nonnegative(),
    /** Adjacent-seq pairs present on this page whose prev→curr link held. */
    links_checked: z.number().int().nonnegative(),
    /** True when the page's seq values form an unbroken run. */
    contiguous: z.boolean(),
    /** True only when seq 1 is on this page. Nothing else proves descent. */
    anchored_to_genesis: z.boolean(),
    /** Every recomputation and every checked link held. */
    ok: z.boolean(),
    /** First seq that failed, when `ok` is false. */
    break_at_seq: AuditSeq.optional(),
    /** `seq_gap` | `prev_hash_mismatch` | `curr_hash_mismatch`. */
    reason: z.string().optional(),
  })
  .openapi("AuditPageIntegrity");

export const AuditEntryListResponse = z
  .object({
    items: z.array(AuditEntry),
    next_cursor: z.string().nullable(),
    chain: AuditPageIntegrity,
  })
  .openapi("AuditEntryListResponse");

/**
 * The answer to "is my chain intact?", over a CONTIGUOUS UNFILTERED seq range.
 *
 * `anchored_to_genesis` is the honest half of the answer: a range starting above
 * seq 1 is internally consistent and nothing about it proves it descends from
 * the genesis row. `complete` says whether the range reached the chain's head.
 * When a chain is longer than one call's cap, `next_from_seq` walks it.
 */
export const AuditChainVerification = z
  .object({
    scope: z.literal("range"),
    from_seq: AuditSeq,
    /** The last seq walked. **0 whenever no row was walked at all** — an empty
     * chain, or a range that began past the head. */
    to_seq: z.number().int().nonnegative(),
    /** Rows actually walked. */
    length: z.number().int().nonnegative(),
    /** The chain's current head seq (0 when empty) — the range's denominator. */
    head_seq: z.number().int().nonnegative(),
    ok: z.boolean(),
    break_at_seq: AuditSeq.optional(),
    reason: z.string().optional(),
    /** True only when a row at seq 1 was actually walked and its `prev_hash`
     * IS the 32-zero-byte genesis link. False when the range starts above 1,
     * when nothing was walked, and when the genesis link failed — anchoring is
     * a claim about a row that was checked, never about a requested bound. */
    anchored_to_genesis: z.boolean(),
    /** `to_seq === head_seq` — nothing after this range is unchecked. */
    complete: z.boolean(),
    /**
     * Where to resume, or **null when there is nothing further to walk** —
     * either the range reached the head (`complete`) or it began past it. A
     * client loop must terminate on null, not on `complete`.
     *
     * The resume point OVERLAPS the last verified row by one on purpose: a
     * cursor of `to_seq + 1` would never check the link ACROSS the chunk
     * boundary, so a splice at exactly that seq would survive a full walk with
     * every response saying `ok: true`.
     */
    next_from_seq: AuditSeq.nullable(),
  })
  .openapi("AuditChainVerification");

/** Rows a single verify call will walk before it truncates and asks for a resume. */
export const AUDIT_VERIFY_MAX_ROWS = 5000;

/**
 * Problem codes this surface returns beyond the shared set.
 *
 * `AUDIT_GROUP_SCOPE_REQUIRED` is a 403 rather than a filtered 200 on purpose:
 * `audit_log` has no `facility_id` and its RLS is tenant-keyed only, so a
 * facility-scoped principal cannot be served a correctly fenced subset. A
 * surface that silently dropped rows an auditor believes are all the rows would
 * be worse than one that refuses.
 */
export const AUDIT_PROBLEM_CODES = ["AUDIT_GROUP_SCOPE_REQUIRED", "AUDIT_RANGE_INVALID"] as const;

export type AuditProblemCode = (typeof AUDIT_PROBLEM_CODES)[number];
